<template>
  <iColumn
    v-click-outside="() => $emit('close')"
    :min-width="100"
    class="dynamic-component-contextual-menu"
    height="hug"
    width="hug"
    :theme-id="0"
  >
    <iMenu
      hover-to-open-sub-menu
      :items="dropdownItems"
      orientation="vertical"
      submenu-style="floating"
      :active-item="showSubMenu"
      @click="handleAction"
    />
  </iColumn>
</template>

<script>
import { MenuConstants } from "@vcl";
import { mapState, mapActions } from "pinia";
import { useDynamicComponentEditorStore } from "%/dynamic-component-editor/stores/dynamic-component-editor";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import BUILDER_Z_INDEX from "%/dynamic-component-editor/constants/builder-z-index";

// Selection ids are breakpoint-qualified ("2603:default"); this component's
// own `elementId` prop is bare — compare via this on both sides.
function bareId(id) {
  return String(id).split(":")[0];
}

export default {
  name: "DynamicComponentContextualMenu",
  props: {
    elementId: {
      type: String,
      required: true,
    },
  },
  emits: ["close"],
  data() {
    return {
      showSubMenu: "",
    };
  },
  computed: {
    // Builder popup tier — keeps the menu + its floating submenu in the shared
    // popup band so GPU edit chrome never paints over them. See builder-z-index.js.
    builderPopupZIndex() {
      return BUILDER_Z_INDEX.BUILDER_POPUP;
    },
    ...mapState(useDynamicComponentEditorStore, [
      "currentlySelectedElementIds",
      "rootElementIdentifier",
      "selectionSharesSameParent",
    ]),

    element() {
      return DynamicElement.get(this.elementId);
    },
    isRootLevel() {
      return this.elementId === this.rootElementIdentifier;
    },
    selectedBareIds() {
      return this.currentlySelectedElementIds.map(bareId);
    },
    isMultiSelection() {
      const hasMultipleSelected = this.currentlySelectedElementIds.length > 1;
      const thisElementIsSelected = this.selectedBareIds.includes(bareId(this.elementId));
      return hasMultipleSelected && thisElementIsSelected;
    },
    isTemplatedComponent() {
      return !!this.element?.template;
    },
    isComponentInstance() {
      const type = this.element?.type;
      return this.isTemplatedComponent
        || type === "DynamicComponentElement"
        || type === "TemplatedComponentElement";
    },
    templateComponentEditUrl() {
      if (!this.isComponentInstance || this.isRootLevel) {
        return "";
      }
      const dcId = this.element?.dynamicComponentId;
      const destinationId = dcId ? DynamicComponent.get(dcId)?.destinationId : null;
      if (!destinationId) {
        return "";
      }
      const referencedRaw = Number(this.element?.getRawOptionValue?.("referenced-component-id", "default", "default")?.r);
      const targetId = this.element?.elementTemplateId
        || (Number.isFinite(referencedRaw) && referencedRaw > 0 ? referencedRaw : 0);
      if (!targetId) {
        return "";
      }
      return `/build/${destinationId}/components/${targetId}`;
    },
    // True when the right-clicked element is a Feed pointing at a
    // destination-scoped DynamicComponent via `feed-row-component-id`.
    // Surfaces the "Edit Component" entry that jumps straight to that
    // DC's edit page — mirrors the inspector header's behaviour so the
    // two affordances stay in sync.
    feedRowDcId() {
      if (this.element?.type !== "Feed") {
        return 0;
      }
      const raw = Number(this.element?.getRawOptionValue?.("feed-row-component-id", "default", "default")?.r);
      return Number.isFinite(raw) && raw > 0 ? raw : 0;
    },
    feedRowDcEditUrl() {
      if (!this.feedRowDcId) {
        return "";
      }
      // DynamicElement exposes the owning DC id; the destination id
      // hangs off the DC instance. Looking it up through
      // `DynamicComponent.get(id)` matches InspectorHeaderV2's
      // `dynamicComponentInstance.destinationId` pattern.
      const dcId = this.element?.dynamicComponentId;
      const destinationId = dcId ? DynamicComponent.get(dcId)?.destinationId : null;
      if (!destinationId) {
        return "";
      }
      return `/build/${destinationId}/components/${this.feedRowDcId}`;
    },
    isFirefox() {
      return navigator.userAgent.toLowerCase().includes("firefox");
    },
    shortcutParentKey() {
      return this.getOS() === "windows" ? "ctrl" : "⌘";
    },
    isElementDeletable() {
      return this.checkIsDeletable(this.elementId);
    },
    isElementDuplicatable() {
      return this.checkIsDuplicatable(this.elementId);
    },
    canWrapElement() {
      return this.checkCanWrap(this.elementId);
    },
    canWrapSelection() {
      return this.isMultiSelection && this.currentlySelectedElementIds.every(id => this.checkCanWrap(id));
    },
    dropdownItems() {
      const items = [];

      const editItem = {
        name: "edit",
        label: "Edit",
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "edit",
      };

      const copyItem = {
        name: "copy",
        label: "Copy",
        secondaryLabel: `${this.shortcutParentKey} + C`,
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "copy",
      };

      const pasteItem = {
        name: "paste",
        label: "Paste",
        secondaryLabel: `${this.shortcutParentKey} + V`,
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "paste",
      };

      const duplicateItem = {
        name: "duplicate",
        label: "Duplicate",
        secondaryLabel: `${this.shortcutParentKey} + D`,
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "duplicate",
      };

      const deleteItem = {
        name: "delete",
        label: "Delete",
        secondaryLabel: "⌫",
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "delete",
      };

      // General Actions
      items.push(editItem);

      // Edit Component: only on a Feed whose row template is a picked
      // destination-scoped DC. Mirrors the InspectorHeaderV2 entry so
      // right-click and the "..." dropdown both jump to the same URL.
      if (this.feedRowDcEditUrl) {
        items.push({
          name: "editComponent",
          label: "Edit Component",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "editComponent",
        });
      }

      // Wrapping Actions
      if (this.canWrapElement && !this.canWrapSelection && !this.isRootLevel) {
        items.push({
          name: "wrap",
          label: "Wrap",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "wrap",
          subItems: [
            { name: "wrapInVerticalContainer", label: "Wrap in Vertical Container", actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK, actionValue: "wrapInVerticalContainer" },
            { name: "wrapInHorizontalContainer", label: "Wrap in Horizontal Container", actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK, actionValue: "wrapInHorizontalContainer" },
          ],
        });
      }

      // Multi-Selection Wrapping Actions. Greyed (not omitted) when the selection
      // spans different parents — you can only wrap elements that share a parent.
      if (this.canWrapSelection && !this.isRootLevel) {
        const wrapDisabled = !this.selectionSharesSameParent;
        const wrapDisabledReason = wrapDisabled ? "Select elements in the same container to wrap them" : undefined;
        items.push({
          name: "wrapSelection",
          label: "Wrap Selection",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "wrap",
          subItems: [
            { name: "wrapSelectionInHorizontalContainer", label: "Wrap selection in Horizontal Container", actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK, actionValue: "wrapSelectionInHorizontalContainer", disabled: wrapDisabled, disabledReason: wrapDisabledReason },
            { name: "wrapSelectionInVerticalContainer", label: "Wrap selection in Vertical Container", actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK, actionValue: "wrapSelectionInVerticalContainer", disabled: wrapDisabled, disabledReason: wrapDisabledReason },
          ],
        });
      }

      // Detach Template
      if (this.isTemplatedComponent) {
        items.push({
          name: "detach",
          label: "Detach",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "detach",
        });
      }

      // Create Template from Element
      // if (!this.isRootLevel) {
      //   items.push({
      //     name: "createNewTemplateFromHere",
      //     label: "Create New Template From Here",
      //     actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
      //     actionValue: "createNewTemplateFromHere",
      //   });
      // }

      // Clipboard Actions
      if (!this.isFirefox) {
        items.push(copyItem);
        items.push(pasteItem);
      }

      // Mutation Actions
      if (this.isElementDuplicatable) {
        items.push(duplicateItem);
      }
      if (this.isElementDeletable) {
        items.push(deleteItem);
      }

      return items;
    },
  },
  methods: {
    ...mapActions(useDynamicComponentEditorStore, {
      getOS: "getOS",
      handleElementSelection: "handleElementSelection",
      handleClipboardElementTransfer: "handleClipboardElementTransfer",
      handleClipboardElementPaste: "handleClipboardElementPaste",
      handleDuplicate: "handleDuplicate",
      handleOnDelete: "handleOnDelete",
      wrapSelectionInContainer: "wrapSelectionInContainer",
      handleDetach: "handleDetach",
      createDynamicComponentFromIdentifier: "createDynamicComponentFromIdentifier",
      checkIsDeletable: "isDeletable",
      checkIsDuplicatable: "isDuplicatable",
      checkCanWrap: "canWrap",
    }),
    navigateToBuilderUrl(url) {
      const navStore = this.$pinia?._s?.get?.("studio-navigation-store");
      if (navStore?.navigateInSpa) {
        navStore.navigateInSpa(url);
      } else {
        window.location.href = url;
      }
    },
    handleAction(action) {
      const targetIds = this.isMultiSelection ? this.currentlySelectedElementIds : [this.elementId];
      const singleTargetId = targetIds[0];

      switch (action) {
        case "edit":
          if (this.templateComponentEditUrl) {
            this.navigateToBuilderUrl(this.templateComponentEditUrl);
            break;
          }
          this.handleElementSelection(this.elementId, false);
          break;
        case "copy":
          if (!this.selectedBareIds.includes(bareId(this.elementId))) {
            this.handleElementSelection(this.elementId, false);
          }
          this.handleClipboardElementTransfer("copy");
          break;
        case "paste":
          if (!this.selectedBareIds.includes(bareId(this.elementId))) {
            this.handleElementSelection(this.elementId, false);
          }
          this.handleClipboardElementPaste();
          break;
        case "duplicate":
          this.handleDuplicate(singleTargetId);
          break;
        case "delete":
          if (this.isMultiSelection) {
            this.handleOnDelete(); // Acts on all currentlySelectedElementIds
          } else {
            this.handleOnDelete(singleTargetId);
          }
          break;
        case "wrapInVerticalContainer":
        case "wrapSelectionInVerticalContainer":
          this.wrapSelectionInContainer("VerticalContainerWrapper", targetIds);
          break;
        case "wrapInHorizontalContainer":
        case "wrapSelectionInHorizontalContainer":
          this.wrapSelectionInContainer("HorizontalContainerWrapper", targetIds);
          break;
        case "detach":
          this.handleDetach(singleTargetId);
          break;
        case "createNewTemplateFromHere":
          this.createDynamicComponentFromIdentifier(singleTargetId);
          break;
        case "editComponent":
          if (this.feedRowDcEditUrl) {
            this.navigateToBuilderUrl(this.feedRowDcEditUrl);
          }
          break;
      }

      // Handle submenu UX logic
      if (action === "wrap") {
        this.showSubMenu = this.showSubMenu === "wrap" ? "" : "wrap";
      } else {
        this.showSubMenu = "";
        this.$emit("close");
      }
    },
  },
  styleGuide: () => ({
    backgroundColor: { "color.background": "paper" },
    borderColor: { "color.border": "dark" },
    borderWidth: { "size.border": "thin" },
    borderRadius: { "size.borderRadius": "large" },

    gap: { "size.spacing": "standard" },

    marginTop: { "size.spacing": "small" },
    marginBottom: { "size.spacing": "small" },
    marginRight: { "size.spacing": "small" },
    marginLeft: { "size.spacing": "small" },

    menuItemFontSize: { "size.font": "small" },
    menuItemFontColor: { "color.font": "standard" },
    menuItemFontWeight: { "font.weight": "standard" },
    menuItemHoverBackgroundColor: { "color.background": "subtle" },
    menuItemPaddingTop: { "size.spacing": "none" },
    menuItemPaddingBottom: { "size.spacing": "none" },
    menuItemPaddingRight: { "size.spacing": "none" },
    menuItemPaddingLeft: { "size.spacing": "none" },

    menuPadding: { "size.spacing": "standard" },
  }),
};
</script>

<style lang="scss" scoped>
.dynamic-component-contextual-menu.col {
  border: 1px solid v-bind('$getStyles.borderColor') !important;
  border-radius: v-bind('$getStyles.borderRadius') !important;
  background-color: v-bind('$getStyles.backgroundColor') !important;
  z-index: v-bind('builderPopupZIndex');
  padding: v-bind("$getStyles.menuPadding") !important;
}

:deep(.submenu.menu) {
  border: 1px solid v-bind('$getStyles.borderColor') !important;
  border-radius: v-bind('$getStyles.borderRadius') !important;
  background-color: v-bind('$getStyles.backgroundColor') !important;
  z-index: v-bind('builderPopupZIndex');
}

:deep(.menu-item) {
  width: 100%;
  border-radius: v-bind('$getStyles.borderRadius') !important;
  padding-top: v-bind('$getStyles.menuItemPaddingTop');
  padding-bottom: v-bind('$getStyles.menuItemPaddingBottom');
  padding-right: v-bind('$getStyles.menuItemPaddingRight');
  padding-left: v-bind('$getStyles.menuItemPaddingLeft');

  &:hover {
    cursor: pointer;
    background-color: v-bind('$getStyles.menuItemHoverBackgroundColor');
  }
}
</style>
